import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Account } from '@feathr/blackbox';
import { Button } from '@feathr/components';
import { useUser } from '@feathr/extender/state';

import * as styles from './FavoriteCell.css';

export interface IFavoriteCellProps {
  original: Account;
}

/**
 * Star toggle for the accounts table's favorite column. Adds or removes the row's account from
 * the signed-in user's `favorite_account_ids` setting and persists it immediately, so the
 * Favorites filter on this page and the account switcher's favorites tab both reflect the change
 * without a reload.
 */
function FavoriteCell({ original }: Readonly<IFavoriteCellProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();

  const favorites: string[] = user.getSetting('favorite_account_ids') || [];
  const isFavorite = favorites.includes(original.id);

  async function handleClick(): Promise<void> {
    user.setSetting(
      'favorite_account_ids',
      isFavorite ? favorites.filter((value) => value !== original.id) : [...favorites, original.id],
    );
    await user.patchDirty();
  }

  return (
    <Button
      aria-label={isFavorite ? t('Remove from favorites') : t('Add to favorites')}
      className={styles.favorite}
      name={'account-favorite-toggle'}
      onClick={handleClick}
      type={'naked'}
    >
      <FontAwesomeIcon
        className={classNames({ [styles.favoriteSelected]: isFavorite })}
        icon={faStar}
      />
    </Button>
  );
}

export default observer(FavoriteCell);
